import React, { useState, Fragment } from 'react'
import { graphql } from 'gatsby'
import style from './card.mod.scss'
import Img from 'gatsby-image'
import { Content, Button, Modal } from '@components'
import { TransitionLink } from '@helpers/pageTransitions'

const CardSubheading = ({ subheading, onClick }) => (
  <p className={style.card__subheading}>
    {Array.isArray(subheading) && subheading.length > 0
      ? subheading.map((item, i) => (
          <Fragment key={`category-${i}`}>
            <Button onClick={onClick} unstyled children={item.title} />
            {i !== subheading.length - 1 && ', '}
          </Fragment>
        ))
      : subheading}
  </p>
)

const SaleSubheading = ({ isPrice, wasPrice }) => (
  <p className={style.card__subheading}>
    <span className={style.card__subheading_was}>{wasPrice}</span>
    <span className={style.card__subheading_is}>{isPrice}</span>
  </p>
)

const Card = ({
  id,
  image,
  gif,
  contentNode,
  date,
  subheading,
  title,
  link,
  horizontal = false,
  flip = false,
  clickable = true,
  wide = false,
  wideButton = false,
  roundImage = false,
  surfaceless = false,
  elevated = false,
  textCentre = false,
  centreInContainer = false,
  minusGuttersFromWidth = false,
  slim = false,
  video,
  button = true,
  largeSubheading = false,
  onClick,
  sale
}) => {
  const [showGif, setShowGif] = useState(false)

  const modifiers = []

  horizontal && modifiers.push(`${style.card}--horizontal`)
  flip && modifiers.push(`${style.card}--flip`)
  wide && modifiers.push(`${style.card}--wide`)
  roundImage && modifiers.push(`${style.card}--round-image`)
  surfaceless && modifiers.push(`${style.card}--surfaceless`)
  elevated && modifiers.push(`${style.card}--elevated`)
  textCentre && modifiers.push(`${style.card}--text-centre`)
  slim && modifiers.push(`${style.card}--slim`)
  largeSubheading && modifiers.push(`${style.card}--large-subheading`)
  centreInContainer && modifiers.push(`${style.card}--centre-in-container`)
  minusGuttersFromWidth &&
    modifiers.push(`${style.card}--minus-gutters-from-width`)

  const cardContent = () => (
    <>
      <div
        className={style.card__image}
        onMouseEnter={() => gif && setShowGif(true)}
        onMouseLeave={() => gif && setShowGif(false)}
        role="presentation"
      >
        <Img {...image} />
        {gif && showGif && (
          <div className={style.card__gif}>
            <img src={gif.url} alt={gif.alt} />
          </div>
        )}
        {video && (
          <Modal video={video} placement={roundImage && `bottom-center`} />
        )}
      </div>
      <div className={style.card__content}>
        {title && <h4>{title}</h4>}
        {date && <p className={style.card__date}>{date}</p>}
        {subheading && !sale && (
          <CardSubheading subheading={subheading} onClick={onClick} />
        )}
        {sale && <SaleSubheading {...sale} />}
        {(contentNode?.childMarkdownRemark?.html ||
          contentNode?.childMarkdownRemark?.excerpt) && (
          <Content contentNode={contentNode} />
        )}
        {(link?.link?.path || link?.url) && button && (
          <div className={style.card__button}>
            <Button to={link?.link?.path || link?.url} wide={wideButton}>
              {link.displayText}
            </Button>
          </div>
        )}
      </div>
    </>
  )

  return (
    <>
      {clickable ? (
        <TransitionLink
          to={link?.link?.path || link?.url}
          className={[style.card, ...modifiers].join(' ')}
        >
          {cardContent()}
        </TransitionLink>
      ) : (
        <div className={[style.card, ...modifiers].join(' ')}>
          {cardContent()}
        </div>
      )}
    </>
  )
}

export default Card

export const query = graphql`
  fragment CardSlide on DatoCmsCardSlide {
    id
    contentNode {
      childMarkdownRemark {
        html
      }
    }
    image {
      fluid(maxWidth: 370, imgixParams: { fit: "crop", w: "370", h: "415" }) {
        ...GatsbyDatoCmsFluid
      }
    }
    internalTitle
    link {
      ...Link
    }
  }

  fragment Card on DatoCmsCard {
    contentNode {
      childMarkdownRemark {
        html
      }
    }
    id
    image {
      fluid(maxWidth: 370, imgixParams: { fit: "crop", w: "370", h: "360" }) {
        ...GatsbyDatoCmsFluid
      }
    }
    gif {
      alt
      url
    }
    title
    link {
      ...Link
    }
  }

  fragment PersonCard on DatoCmsPersonSlide {
    id
    name
    descriptionNode {
      childMarkdownRemark {
        html
      }
    }
    image {
      fluid(maxWidth: 368, imgixParams: { fit: "crop", w: "368", h: "368" }) {
        ...GatsbyDatoCmsFluid
      }
    }
    mobileImage: image {
      fluid(maxWidth: 240, imgixParams: { fit: "crop", w: "240", h: "240" }) {
        ...GatsbyDatoCmsFluid
      }
    }
    link {
      ...Link
    }
    video {
      url
      title
      provider
      providerUid
      thumbnailUrl
      width
      height
    }
  }

  fragment PostCard on DatoCmsPost {
    id
    title
    path
    excerptNode {
      childMarkdownRemark {
        excerpt(truncate: true, pruneLength: 225)
      }
    }
    categories {
      title
      slug
      id
    }
    meta {
      publishedAt
    }
    eventTime
    eventLocation
    image {
      fluid(maxWidth: 740, imgixParams: { fit: "crop", w: "740", h: "830" }) {
        ...GatsbyDatoCmsFluid
      }
    }
    wideImage: image {
      fluid(maxWidth: 1140, imgixParams: { fit: "crop", w: "1140", h: "910" }) {
        ...GatsbyDatoCmsFluid
      }
    }
  }
`
